'use client';

import cx from 'classnames';
import type {Delta} from 'quill';
import type {AlignClass} from 'quill/formats/align';
import type {FontClass} from 'quill/formats/font';
import type IndentConfig from 'quill/formats/indent';
import type LinkConfig from 'quill/formats/link';
import type {SizeClass} from 'quill/formats/size';
import type IconsDict from 'quill/ui/icons';
import MagicUrl from 'quill-magic-url';
import {
  type CSSProperties,
  type FC,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactQuill, {type DeltaStatic, type EmitterSource, Quill} from 'react-quill-new';

import {Typography} from '../atoms/Typography';
import {AddLinkIcon} from '../svg/v2/AddLinkIcon';
import {BoldTextIcon} from '../svg/v2/BoldTextIcon';
import {BulletedListIcon} from '../svg/v2/BulletedListIcon';
import {ItalicTextIcon} from '../svg/v2/ItalicTextIcon';
import {NumberedListIcon} from '../svg/v2/NumberedListIcon';
import {RedoIcon} from '../svg/v2/RedoIcon';
import {UnderlineTextIcon} from '../svg/v2/UnderlineTextIcon';
import {UndoIcon} from '../svg/v2/UndoIcon';
import {validUrlInTextRegex} from '../utils/utils';

import 'react-quill-new/dist/quill.snow.css';

const Parchment = Quill.import('parchment');

// Configure styles
const SizeStyle: typeof SizeClass = Quill.import('attributors/style/size') as typeof SizeClass;
SizeStyle.whitelist = undefined;
Quill.register(SizeStyle, true);

// Configure line height options
const lineHeightConfig = {
  scope: Parchment.Scope.BLOCK,
};
const lineHeightClass = new Parchment.ClassAttributor(
  'line-height',
  'ql-line-height',
  lineHeightConfig
);
const lineHeightStyle = new Parchment.StyleAttributor(
  'line-height',
  'line-height',
  lineHeightConfig
);
Quill.register(lineHeightClass, true);
Quill.register(lineHeightStyle, true);
Quill.register({'attributors/class/line-style': lineHeightClass}, true);
Quill.register({'attributors/style/line-style': lineHeightStyle}, true);
Quill.register({'formats/line-height': lineHeightStyle}, true);

// Configure font weight options
const fontWeightConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: [
    '',
    'normal',
    'bold',
    'bolder',
    'light',
    'lighter',
    'inherit',
    'initial',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
  ],
};
const fontWeightStyle = new Parchment.StyleAttributor(
  'font-weight',
  'font-weight',
  fontWeightConfig
);
Quill.register(fontWeightStyle, true);
Quill.register({'attributors/style/font-weight': fontWeightStyle}, true);
Quill.register({'formats/font-weight': fontWeightStyle}, true);
Quill.register('modules/magicUrl', MagicUrl);

// Configure text transform options
const textTransformConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: ['none', 'capitalize', 'uppercase', 'lowercase'],
};
const textTransformStyle = new Parchment.StyleAttributor(
  'texttransform',
  'text-transform',
  textTransformConfig
);
Quill.register(textTransformStyle, true);
Quill.register({'attributors/style/text-transform': textTransformStyle}, true);
Quill.register({'formats/text-transform': textTransformStyle}, true);

// Configure margin options
const marginConfig = {
  scope: Parchment.Scope.BLOCK,
};
const marginStyle = new Parchment.StyleAttributor('margin', 'margin', marginConfig);
Quill.register(marginStyle, true);
Quill.register({'attributors/style/margin': marginStyle}, true);
Quill.register({'formats/margin': marginStyle}, true);

// Configure line alignment options
const AlignStyle: typeof AlignClass = Quill.import('attributors/style/align') as typeof AlignClass;
const Icons: typeof IconsDict = Quill.import('ui/icons') as typeof IconsDict;
Icons.bold = ReactDOMServer.renderToString(<BoldTextIcon ratio={0.75} />);
Icons.italic = ReactDOMServer.renderToString(<ItalicTextIcon ratio={0.75} />);
Icons.underline = ReactDOMServer.renderToString(<UnderlineTextIcon ratio={0.75} />);
Icons.list.bullet = ReactDOMServer.renderToString(<BulletedListIcon ratio={0.75} />);
Icons.list.ordered = ReactDOMServer.renderToString(<NumberedListIcon ratio={0.75} />);
Icons.link = ReactDOMServer.renderToString(<AddLinkIcon ratio={0.75} />);
AlignStyle.whitelist = ['left', 'center', 'right', 'justify'];
Quill.register(AlignStyle, true);

const Indent: typeof IndentConfig = Quill.import('formats/indent') as typeof IndentConfig;
Indent.whitelist = ['1', '2'];
Quill.register(Indent, true);

// Configure link sanitation
const Link: typeof LinkConfig = Quill.import('formats/link') as typeof LinkConfig;
const builtInSanitize = Link.sanitize;
Link.sanitize = function customSanitizeLinkInput(link) {
  let val = link;

  // if URL starts with a custom protocol, do nothing
  // otherwise check for http/https
  if (!/^\w+:/.test(val)) {
    if (!/^http:\/\/?:/.test(val) || !/^https:\/\/?:/.test(val)) {
      val = `https://${val}`;
    }
  }
  return builtInSanitize.call(this, val);
};

interface TextEditorProps {
  containerClassName?: string;
  innerQuillClassName?: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?(
    selection: ReactQuill.Range,
    source: EmitterSource,
    editor: ReactQuill.UnprivilegedEditor
  ): void;
  onBlur?(
    previousSelection: ReactQuill.Range,
    source: EmitterSource,
    editor: ReactQuill.UnprivilegedEditor
  ): void;
  toolbarId?: string;
  scrollingContainer?: string;
  style?: CSSProperties;
  id?: string;
  label?: string;
  error?: boolean;
  'data-qa-id'?: string;
  boundingContainerID?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  allowUnderline?: boolean;
  minRows?: number;
  maxRows?: number;
}

export const TextEditor: FC<TextEditorProps> = ({
  containerClassName,
  innerQuillClassName,
  value,
  onChange,
  onFocus,
  onBlur,
  label,
  error = false,
  toolbarId,
  allowUnderline = false,
  style,
  id,
  'data-qa-id': dataQaId,
  boundingContainerID,
  placeholder,
  maxLength,
  disabled = false,
  minRows = 3,
  maxRows = 13,
}) => {
  const reactQuillRef = useRef<ReactQuill>(null);
  const valueLoaded = useRef(false);
  const [editorContent, setEditorContent] = useState(value || '');
  const [undoStackSize, setUndoStackSize] = useState(0);
  const [redoStackSize, setRedoStackSize] = useState(0);
  const prevEditorState = useRef<DeltaStatic>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing functionality; refactor needed
  useEffect(() => {
    if (valueLoaded.current || !value) return;
    if (value !== editorContent) {
      setEditorContent(value);
    }
    valueLoaded.current = true;
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: save the previous editor state whenever editorContent changes
  useEffect(() => {
    const editor = reactQuillRef.current?.getEditor();
    prevEditorState.current = editor?.getContents() ?? null;
  }, [editorContent]);

  // Reset history on mount so the user cannot undo to the empty state of the editor
  // (prior to initialization of the current value)
  useEffect(() => {
    if (!reactQuillRef.current) return;
    const editor = reactQuillRef.current?.getEditor();
    editor.history.clear();
    setUndoStackSize(editor.history.stack.undo.length);
    setRedoStackSize(editor.history.stack.redo.length);
  }, []);

  const modules: ReactQuill.QuillOptions['modules'] = {
    toolbar: {
      container: `#${toolbarId}` || '#quill-toolbar',
      clipboard: {
        matchVisual: false,
      },
    },
    magicUrl: {
      urlRegularExpression: validUrlInTextRegex,
      globalRegularExpression: validUrlInTextRegex,
    },
  };

  const FontStyle: typeof FontClass = Quill.import('attributors/style/font') as typeof FontClass;
  // FontStyle.whitelist = ['Brut', ...props.fonts];
  delete FontStyle.whitelist;
  Quill.register(FontStyle, true);

  useEffect(() => {
    if (!reactQuillRef.current) return;
    const editor = reactQuillRef.current.getEditor().root;
    editor.style.minHeight = `${minRows * 1.375}rem`; // 1.375rem is the line height
    editor.style.maxHeight = `${maxRows * 1.375}rem`;
    editor.dataset.qaId = dataQaId;
  }, [dataQaId, minRows, maxRows]);

  const onChangeHandler = useCallback(
    (newValue: string, _delta: Delta, source: EmitterSource) => {
      if (source !== 'user') return;
      if (!reactQuillRef.current) {
        setEditorContent(newValue);
        onChange(newValue);
        return;
      }
      const editor = reactQuillRef.current.getEditor();
      const plainText = editor.getText(); // Get plain text without HTML tags
      const semanticHTML = editor.getSemanticHTML();

      if (maxLength && plainText.length > maxLength + 1 && prevEditorState.current) {
        // maxLength + 1 accounts for trailing newline in maxLength
        // If content exceeds maxLength, keep existing editor state and do not allow change
        editor.setContents(prevEditorState.current);
      } else {
        setEditorContent(newValue);
        onChange(semanticHTML.replace(/&nbsp;/g, ' '));
        setUndoStackSize(editor.history.stack.undo.length);
        setRedoStackSize(editor.history.stack.redo.length);
      }
    },
    [maxLength, onChange]
  );

  const handleUndo = useCallback(() => {
    if (!reactQuillRef.current) return;
    const editor = reactQuillRef.current.getEditor();
    editor.history.undo();
    setUndoStackSize(editor.history.stack.undo.length);
    setRedoStackSize(editor.history.stack.redo.length);
  }, []);

  const handleRedo = useCallback(() => {
    if (!reactQuillRef.current) return;
    const editor = reactQuillRef.current.getEditor();
    editor.history.redo();
    setUndoStackSize(editor.history.stack.undo.length);
    setRedoStackSize(editor.history.stack.redo.length);
  }, []);

  const preventBlurOnUndoRedoMouseDown: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const formats = useMemo(() => {
    const baseFormats = ['bold', 'italic', 'list', 'link', 'indent'];
    if (allowUnderline) baseFormats.push('underline');
    return baseFormats;
  }, [allowUnderline]);

  return (
    <div className={cx('text-editor__container', {error}, containerClassName)}>
      <div className="text-editor__toolbar">
        {label && (
          <Typography variant="paragraph2" color="pebble" className="text-editor__toolbar__label">
            {label}
          </Typography>
        )}
        <div className="text-editor__toolbar__options" id={toolbarId}>
          <div className="text-editor__toolbar__options text-editor__toolbar__options--undo-redo">
            <button
              type="button"
              aria-label="undo"
              onMouseDown={preventBlurOnUndoRedoMouseDown}
              onClick={handleUndo}
              disabled={undoStackSize === 0}
            >
              <UndoIcon ratio={0.75} />
            </button>
            <button
              type="button"
              aria-label="redo"
              onMouseDown={preventBlurOnUndoRedoMouseDown}
              onClick={handleRedo}
              disabled={redoStackSize === 0}
            >
              <RedoIcon ratio={0.75} />
            </button>
          </div>
          <button className="ql-bold" type="button" aria-label="bold" />
          <button className="ql-italic" type="button" aria-label="italic" />
          {allowUnderline && (
            <button className="ql-underline" type="button" aria-label="underline" />
          )}
          <button className="ql-list" value="bullet" type="button" aria-label="bullet list" />
          <button className="ql-list" value="ordered" type="button" aria-label="ordered list" />
          <button className="ql-link" type="button" aria-label="link" />
        </div>
      </div>
      <ReactQuill
        ref={reactQuillRef}
        id={id}
        theme="snow"
        bounds={boundingContainerID || `#${id}`}
        value={editorContent}
        data-qa-id={dataQaId}
        className={cx('text-editor__container__quill', 'paragraph2', innerQuillClassName)}
        onChange={onChangeHandler}
        modules={modules}
        formats={formats}
        style={style}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  );
};
